import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface AppUser {
  id: string
  email: string | null
  created_at: string
  last_sign_in_at: string | null
  is_self: boolean
  household_id: string | null
  household_name: string | null
  role: 'owner' | 'member' | null
  /** Quando entrou no tenant (household_members.created_at); ordena a árvore. */
  member_since?: string
}

export interface TenantInfo {
  id: string
  name: string
  member_count: number
}

interface UsersResponse {
  users: AppUser[]
  tenants: TenantInfo[]
  isSuperAdmin: boolean
  canManage: boolean
}

type Scope = 'tenant' | 'platform'

// Toda gerência de usuário/tenant passa pela Edge Function `admin-users` (service_role
// no servidor). Escopo (via QUERY PARAM — header customizado quebraria o preflight CORS
// do gateway do Supabase):
//   'tenant'   → usuários do próprio tenant (Configurações → Usuários).
//   'platform' → todos os usuários/tenants (menu "Adm. usuários", só super-admin).
async function invoke<T>(
  method: 'GET' | 'POST' | 'DELETE',
  body?: Record<string, unknown>,
  scope: Scope = 'tenant',
): Promise<T> {
  const name = method === 'GET' ? `admin-users?scope=${scope}` : 'admin-users'
  const { data, error } = await supabase.functions.invoke(name, { method, body })
  if (error) {
    let msg = error.message
    try {
      const ctx = (error as { context?: Response }).context
      if (ctx) msg = ((await ctx.json()) as { error?: string }).error ?? msg
    } catch {
      /* mantém a mensagem original */
    }
    throw new Error(msg)
  }
  return data as T
}

// Usuários do PRÓPRIO tenant (Configurações → Usuários).
export function useUsers() {
  return useQuery({
    queryKey: ['users', 'tenant'],
    queryFn: () => invoke<UsersResponse>('GET', undefined, 'tenant'),
  })
}

// Todos os usuários/tenants da PLATAFORMA (só super-admin).
export function usePlatformUsers() {
  return useQuery({
    queryKey: ['users', 'platform'],
    queryFn: () => invoke<UsersResponse>('GET', undefined, 'platform'),
  })
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['users'] })
}

// Criar usuário. No escopo tenant (Configurações → Usuários) só precisa de e-mail+senha:
// o servidor adiciona no próprio tenant do chamador. No Adm. usuários (super-admin) o
// servidor cria um tenant NOVO com esse usuário como owner.
export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => invoke('POST', v),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => invoke('DELETE', { id }),
    onSuccess: () => invalidateAll(qc),
  })
}
